import { useState } from 'react';
import Checkbox from 'src/components/primitives/Checkbox/Checkbox';
import Switch from 'src/components/primitives/Switch/Switch';
import useUpdateSubaccountSettings from 'src/hooks/driftActions/user/useUpdateSubaccountSettings';
import useDriftClient from 'src/hooks/useDriftClient';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import { cj } from 'src/utils/classNames';
import { WithClassName } from 'src/utils/UtilityTypes';

interface Props extends WithClassName {
	isAccountMarginEnabled: boolean;
	checked: boolean;
	onCheckedChange: () => void;
}

export default function MarginCheckbox({
	isAccountMarginEnabled,
	checked,
	onCheckedChange,
	className,
}: Props) {
	const tooltip = !isAccountMarginEnabled
		? { content: <EnableMarginTooltip /> }
		: undefined;

	return (
		<Checkbox.Default
			id="trade-form-margin"
			label="Margin"
			checked={isAccountMarginEnabled && checked}
			onCheckedChange={onCheckedChange}
			tooltip={tooltip}
			disabled={!isAccountMarginEnabled}
			className={cj('w-fit', className)}
		/>
	);
}

function EnableMarginTooltip() {
	const driftClient = useDriftClient();
	const isWalletConnected = useWalletIsConnected();
	const isManagedAccount = useDriftAccountStore(
		(s) => !!s.accounts[s.currentUserKey ?? '']?.isDelegatedTo
	);

	const [isSubmitting, setIsSubmitting] = useState(false);
	const { updateSubaccountSettings } = useUpdateSubaccountSettings();

	const enableAccountMargin = async () => {
		if (!driftClient || isSubmitting) {
			return;
		}

		setIsSubmitting(true);

		await updateSubaccountSettings({
			[driftClient.activeSubAccountId]: { marginTradingEnabled: true },
		});

		setIsSubmitting(false);
	};

	const disabled = !isWalletConnected || isManagedAccount;

	return (
		<div className="flex items-center gap-x-2xl typo-t4">
			Trade with up to 5x leverage with spot margin.
			<Switch
				size="sm"
				className="shrink-0"
				onCheckedChange={enableAccountMargin}
				disabled={disabled}
			/>
		</div>
	);
}
